"use client";

import ThemeRegistry from "@/components/Theme/ThemeRegistry";
import { freeEmailDomains } from "@/constants/businessEmail";
import { APP_ROUTES } from "@/constants/routes";
import UseAuth from "@/hooks/useAuth";
import {
  clearErrorMess,
  setErrorMess,
  setLoading,
} from "@/lib/features/statusSlice";
import { useAppDispatch } from "@/lib/hooks";
import { OptionItem } from "@/models/general";
import UserServices from "@/services/main/UserServices";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Step, Stepper } from "@mui/material";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
export interface RegisterForm {
  invitationCode: string;
  firstName: string;
  lastName: string;
  email: string;
  country: {
    value: number | string;
    label: string;
    shortName: string;
  } | null;
  phone: string;
  company: string;
  title: string;
  password: string;
  confirmPassword: string;
}

const steps = [1, 2];

export default function MultiStepForm() {
  const locale = useLocale();
  const searchParams = useSearchParams();
  const token = searchParams.get("t");
  const email = searchParams.get("e");
  const type = searchParams.get("type");
  const redirect = searchParams.get("redirect");
  const [isEmailExisted, setIsEmailExisted] = useState<boolean>(false);
  const [step, setStep] = useState(0);
  const router = useRouter();
  const [historyLog, setHistoryLog] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { isLoggedIn } = UseAuth();
  const t = useTranslations("messages");
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [countries, setCountries] = useState<OptionItem[]>([]);
  const registerStep1Schema = yup.object().shape({
    invitationCode: yup.string().trim().notRequired(),
    firstName: yup
      .string()
      .trim()
      .required(() => t("field_firstname_required"))
      .max(255, () => t("field_firstname_max"))
      .matches(/^[A-Za-zÀ-Ỹà-ỹ\s]+$/, () => t("field_firstname_format")),
    lastName: yup
      .string()
      .trim()
      .required(() => t("field_lastname_required"))
      .max(255, () => t("field_lastname_max"))
      .matches(/^[A-Za-zÀ-Ỹà-ỹ\s]+$/, () => t("field_lastname_format")),
    email: yup
      .string()
      .trim()
      .email(() => t("field_email_vali_business"))
      .test(
        "is-valid-email",
        () => t("field_email_vali_business"),
        (value) => {
          if (!value) return true;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(value);
        }
      )
      .required(() => t("field_email_vali_required"))
      .test(
        "is-business-email",
        () => t("field_email_vali_business"),
        (value) => {
          const domainSplit = value.split("@")[1];
          return (
            !value || !freeEmailDomains.some((domain) => domain === domainSplit)
          );
        }
      ),
    country: yup.object().required(() => t("field_country_vali_required")),
  });
  const registerStep2Schema = yup.object().shape({
    phone: yup
      .string()
      .trim()
      .nullable()
      .typeError(() => t("field_phone_number_vali_phone"))
      .notRequired()
      .test(
        "is-valid-phone",
        () => t("field_phone_number_vali_phone"),
        (value) => !value || isValidPhoneNumber(value)
      ),
    company: yup
      .string()
      .trim()
      .nullable()
      .notRequired()
      .test(
        "is-valid-company",
        () => t("field_company_vali_company"),
        (value) => !value || /^[A-Za-zÀ-Ỹà-ỹ\s]+$/.test(value)
      ),
    title: yup
      .string()
      .trim()
      .test(
        "is-valid-job",
        () => t("field_title_vali_title"),
        (value) => !value || /^[A-Za-zÀ-Ỹà-ỹ\s]+$/.test(value)
      )
      .nullable()
      .notRequired(),
  });

  // const registerStep3Schema = yup.object().shape({
  //   password: yup
  //     .string()
  //     .trim()
  //     .required(() => "Please enter a password.")
  //     // .required(() =>t("field_password_vali_required"))
  //     .min(8, () => t("field_password_vali_password"))
  //     .matches(
  //       /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).*$/,
  //       () => t("field_password_vali_password")
  //     )
  //     .test(
  //       "no-unicode",
  //       "Password must not contain accented or non-ASCII characters.",
  //       (value) => !value || /^[\x00-\x7F]*$/.test(value)
  //     ),
  //   confirmPassword: yup
  //     .string()
  //     .trim()
  //     .oneOf([yup.ref("password")], () => t("field_confirm_password_vali_password"))
  //     .required(() => "Please confirm your password."),
  // });
  useEffect(() => {
    dispatch(setLoading(false));
  }, [dispatch]);

  const schemas = useCallback((): Array<any> => {
    return [
      registerStep1Schema,
      registerStep2Schema,
      // registerStep3Schema,
    ];
  }, []);
  const [subEmail, setSubEmail] = useState<string>("");
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    trigger,
    control,
    formState: { errors },
    clearErrors,
    setError,
    getValues,
    reset,
  } = useForm<RegisterForm>({
    resolver: yupResolver(schemas()[step]),
    mode: "onChange",
    defaultValues: {
      email: email || "",
    },
  });
  const nextStep = () => {
    setStep((prev) => prev + 1);
  };
  const extractDomainAfterAt = (email: string): string => {
    const emailSplit = email.split("@").filter(Boolean);
    const emailParts = emailSplit[1];
    setSubEmail(emailParts);
    return emailParts;
  };

  // const registerMutation = useMutation({
  //   mutationKey: ["register"],
  //   mutationFn: async (data: RegisterForm) => {
  //     return await UserServices.register2(data);
  //   },
  //   onSuccess: async (_, variables) => {
  //     dispatch(setSuccessMess(t("register_success_popup_title")));
  //     setHistoryLog(false);
  //     setSelectRadio(null);
  //     setTypeOrganization(null);
  //     localStorage.removeItem("selectRadio");
  //     localStorage.removeItem("typeOrganization");
  //     try {
  //       //await UserServices.sendVerifyEmail(variables.email);
  //     } catch (error) {
  //       console.error("Lỗi khi gửi email xác thực:", error);
  //     }
  //     router.push(
  //       `${APP_ROUTES.VERIFIED_EMAIL.to}?email=${encodeURIComponent(variables.email)}`
  //     );
  //   },
  //   onError: (error: any) => {
  //     dispatch(setErrorMess(error?.detail));
  //   },
  //   onSettled: () => {
  //     dispatch(setLoading(false));
  //   },
  // });

  const onSubmit = async (data: RegisterForm) => {
    if (step === 0) {
      try {
        const checkHistoryLog = await UserServices.checkHistoryLog(
          token || ""
        ).catch(() => false);
        const { data: checkEmailBlocked } =
          await UserServices.checkEmailBlocked(data.email);
        if (checkEmailBlocked?.isBlocked) {
          setError("email", {
            type: "manual",
            message: t("field_email_blocked_register"),
          });
          return;
        }
        if (checkHistoryLog?.data) {
          setHistoryLog(true);
        }
        nextStep();
      } catch (e: any) {
        // if (e.data?.existed) {
        //   setIsEmailExisted(true);
        //   return;
        // }
        dispatch(clearErrorMess(e.message));
      }
    } else if (step < steps.length - 1) {
      setIsEmailExisted(false);
      nextStep();
    } else {
      setIsLoadingSubmit(true);
      const dataRegister = {
        ...data,
        isAlreadyHaveOrganization: historyLog,
        countryId: (data.country as any)?.value,
      };
      await UserServices.register2(dataRegister)
        .then((res) => {
          router.push(
            `${APP_ROUTES(locale).VERIFIED_OTP.to}?email=${encodeURIComponent(data.email)}${redirect ? `&redirect=${encodeURIComponent(redirect)}` : ""}`
          );
        })
        .catch((e) => dispatch(setErrorMess(e)))
        .finally(() => setIsLoadingSubmit(false));
      // registerMutation.mutate({
      //   ...data,
      //   isAlreadyHaveOrganization: historyLog,
      //   isNewOrganization:
      //     typeOrganization === "create" && selectRadio === "organization",
      //   isJoinOrganization:
      //     typeOrganization === "join" && selectRadio === "organization",
      // });
    }
  };
  function handleStep(index: number) {
    setStep(index);
  }

  useEffect(() => {
    if (isLoggedIn) {
      router.push(APP_ROUTES(locale).HOME.to);
    }
  }, [isLoggedIn, router]);

  const handleRenderHeader = useMemo(() => {
    switch (type) {
      case "plan":
        return t("register_page_plan_title");
      case "report":
        return t("register_page_report_title");
      case "trial":
        return t("register_page_trial_title");
      default:
        return t("register_page_title");
    }
  }, [type, t]);
  return (
    <ThemeRegistry>
      {/* Stepper */}
      <Stepper
        nonLinear
        activeStep={step}
        connector={<></>}
        sx={{
          position: "absolute",
          top: "22px",
          right: "22px",
          display: "flex",
          gap: 1,
        }}
      >
        {steps.map((_, index) => (
          <Step
            key={index}
            completed={index <= step}
            sx={{
              p: 0,
              height: 4,
              width: 24,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                height: 4,
                width: 24,
                borderRadius: 1,
                backgroundColor:
                  index <= step
                    ? "var(--cimigo-blue)"
                    : "var(--cimigo-gray-300)",
                cursor: index < step ? "pointer" : "default",
              }}
              onClick={() => {
                if (index < step) {
                  handleStep(index);
                }
                if (index === 0) {
                  reset({
                    ...getValues(),
                    phone: "",
                    company: "",
                    title: "",
                    password: "",
                    confirmPassword: "",
                  });
                }
              }}
            />
          </Step>
        ))}
      </Stepper>

      {/* Nội dung Step */}
      <Box className="flex flex-col gap-2">
        <p
          className="text-cimigo-blue text-[22px] md:text-[24px] font-semibold leading-[133.333%] m-0"
          translation-key="register_page_title"
          translation-plan-key="register_page_plan_title"
          translation-report-key="register_page_report_title"
        >
          {handleRenderHeader}
        </p>
        <form onSubmit={handleSubmit(onSubmit)}>
          {step === 0 && (
            <Step1
              handleFormSubmit={handleSubmit(onSubmit)}
              type={type}
              setCountries={setCountries}
              countries={countries}
              // isEmailExisted={isEmailExisted}
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              trigger={trigger}
              control={control}
              clearErrors={clearErrors}
              setError={setError}
            />
          )}
          {step === 1 && (
            <Step2
              register={register}
              watch={watch}
              clearErrors={clearErrors}
              errors={errors}
              control={control}
              isLoadingSubmit={isLoadingSubmit}
            />
          )}
          {/* {step === 2 && (
            <Step3
              isLoadingSubmit={isLoadingSubmit}
              register={register}
              errors={errors}
              watch={watch}
            />
          )} */}
        </form>
      </Box>
    </ThemeRegistry>
  );
}
